import type { CityConfig } from "../city-template/types";

export const berkeleyConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Berkeley",
  state: "California",
  stateAbbr: "CA",
  slug: "berkeley",
  route: "/berkeley",
  title: "Emergency Passport Berkeley, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Berkeley, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Alameda County and East Bay travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Berkeley, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Berkeley and East Bay travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Berkeley%20California%20cityscape%20at%20golden%20hour%20with%20the%20University%20of%20California%20Campanile%20clock%20tower%20visible%2C%20historic%20Telegraph%20Avenue%20storefronts%2C%20rolling%20green%20hills%20of%20the%20East%20Bay%20in%20the%20background%2C%20warm%20sunset%20light%20over%20the%20San%20Francisco%20Bay%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20vibrant%20college%20town%20atmosphere&width=1800&height=700&seq=berkeley-hero-001&orientation=landscape",
  heroImageAlt: "Berkeley skyline at golden hour with UC Berkeley Campanile tower and East Bay hills",
  geoPosition: "37.8715;-122.2730",
  geoPlacename: "Berkeley, California",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco, CA 94105",
    distance: "Located in downtown San Francisco — approximately 25–35 minutes from Berkeley via I-80 or BART",
  },
  nearbyCitiesZips: [
    { name: "Berkeley", zips: "94702–94710" },
    { name: "Oakland", zips: "94601–94612" },
    { name: "Emeryville", zips: "94608" },
    { name: "Albany", zips: "94706, 94707" },
    { name: "El Cerrito", zips: "94530" },
    { name: "Richmond", zips: "94801–94805" },
    { name: "San Pablo", zips: "94806" },
    { name: "Pinole", zips: "94564" },
    { name: "Hercules", zips: "94547" },
    { name: "Orinda", zips: "94563" },
    { name: "Lafayette", zips: "94549" },
    { name: "Walnut Creek", zips: "94595–94598" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent East Bay departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Emergency passport help in San Francisco", route: "/san-francisco" },
    { name: "Fast passport renewal in Oakland", route: "/oakland" },
    { name: "Expedited passport services in San Jose", route: "/san-jose" },
    { name: "Urgent passport services in Sacramento", route: "/sacramento" },
    { name: "Same-day passport help near Santa Rosa", route: "/santa-rosa" },
    { name: "Passport courier services near Salinas", route: "/salinas" },
    { name: "Rush passport help in Santa Clara", route: "/santa-clara" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Berkeley remotely via secure courier. We do not have a walk-in office in Berkeley — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for East Bay travelers.",
  fpcOfficeAddress: "447 Sutter St, San Francisco, CA 94108",
  fpcOfficeDistance: "Serving all of Alameda County",
  serviceAreaIntro: "From the UC Berkeley campus to the Berkeley Hills, from Elmwood to North Berkeley — Fast Passport Center serves the entire Berkeley community. We regularly help travelers from Oakland, Emeryville, Albany, and Richmond get their passports fast.",
  nearbyCitiesIntro: "We serve all Berkeley neighborhoods and nearby East Bay cities including Oakland, Emeryville, Albany, El Cerrito, Richmond, and Walnut Creek. If you are in Alameda or Contra Costa County, we have you covered.",
  howItWorksIntro: "Most Berkeley travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With SFO and Oakland International serving as the Bay Area's primary international gateways, Berkeley travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Berkeley and East Bay travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Berkeley? Here are the most common ones we hear from East Bay travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent East Bay international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Thomas H.",
      location: "Berkeley, CA",
      rating: 5,
      text: "My research conference in Tokyo was in 2 days and my passport was expired. Fast Passport Center got me a new one in 30 hours. Saved my academic career.",
      service: "Emergency Passport",
    },
    {
      name: "Priya S.",
      location: "Oakland, CA",
      rating: 5,
      text: "Needed to renew for a family trip to India. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend for East Bay residents.",
      service: "Passport Renewal",
    },
    {
      name: "Marcus J.",
      location: "Richmond, CA",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Greece. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Emily R.",
      location: "Albany, CA",
      rating: 5,
      text: "As a frequent international traveler from the East Bay, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "berkeley-1",
      name: "Berkeley Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2000 Allston Way",
      city: "Berkeley",
      state: "CA",
      zip: "94704",
      phone: "(510) 849-1359",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Berkeley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "berkeley-2",
      name: "Oakland Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1675 7th St",
      city: "Oakland",
      state: "CA",
      zip: "94615",
      phone: "(510) 251-1353",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.0 miles from Jack London Square",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "berkeley-3",
      name: "El Cerrito Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1170 San Pablo Ave",
      city: "El Cerrito",
      state: "CA",
      zip: "94530",
      phone: "(510) 237-4213",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.5 miles from El Cerrito Plaza",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for northern Alameda County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "berkeley-4",
      name: "Richmond Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1025 Nevin Ave",
      city: "Richmond",
      state: "CA",
      zip: "94801",
      phone: "(510) 232-4548",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Richmond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for western Contra Costa County. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "berkeley-5",
      name: "Walnut Creek Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2070 Diablo Blvd",
      city: "Walnut Creek",
      state: "CA",
      zip: "94596",
      phone: "(925) 935-0231",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from Broadway Plaza",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Contra Costa County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "berkeley-6",
      name: "Alameda County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1106 Madison St",
      city: "Oakland",
      state: "CA",
      zip: "94607",
      phone: "(510) 272-6362",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "0.5 miles from Oakland City Hall",
      website: "https://www.acgov.org/",
      websiteLabel: "Alameda County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "UC Berkeley Academic Travel", agency: "No special handling", fpc: "Yes — faculty and researcher expedited processing" },
    { feature: "East Bay BART Access", agency: "Drive to SF — parking hassle", fpc: "No — ship from home, BART-friendly" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "East Bay photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at East Bay acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many East Bay applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many East Bay families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "East Bay travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Berkeley." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Berkeley address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Berkeley before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Berkeley for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most East Bay travelers." },
    { q: "How fast can I renew my passport in Berkeley?", a: "Fast Passport Center offers fast passport renewal in Berkeley in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my Europe trip?", a: "If your passport expires soon before a Europe trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Berkeley in 24–48 hours so you don't miss your flights." },
    { q: "Can I get a same-day passport appointment near Berkeley?", a: "Same-day passport appointments are only available at the SF Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Berkeley residents." },
    { q: "Can I expedite a child passport in Berkeley?", a: "Yes. Fast Passport Center expedites child passports in Berkeley for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk East Bay families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Berkeley with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Berkeley?",
      a: "With Fast Passport Center's emergency courier service, Berkeley travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Berkeley?",
      a: "Popular options include the Berkeley Main Post Office (94704), Oakland Main Post Office (94615), El Cerrito Post Office (94530), and Alameda County Clerk (94607). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Berkeley?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in the East Bay suburbs or Contra Costa County?",
      a: "We serve all of Alameda and Contra Costa counties including Walnut Creek, Richmond, El Cerrito, San Pablo, and Pinole. Our overnight shipping labels work from any UPS location, so your location within the East Bay doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Berkeley?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Berkeley?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Berkeley and Oakland offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Berkeley?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Berkeley?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk East Bay families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Berkeley", address: "2101 Shattuck Ave, Berkeley, CA 94704", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walgreens — Oakland", address: "2700 Broadway, Oakland, CA 94612", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/" },
    { name: "FedEx Office — Berkeley", address: "1838 Euclid Ave, Berkeley, CA 94709", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.fedex.com/" },
    { name: "UPS Store — El Cerrito", address: "10780 San Pablo Ave, El Cerrito, CA 94530", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/" },
    { name: "Walmart — Richmond", address: "1400 Hilltop Mall Rd, Richmond, CA 94806", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/" },
    { name: "Rite Aid — Albany", address: "1189 Solano Ave, Albany, CA 94706", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/" },
    { name: "Postal Annex — Walnut Creek", address: "1522 Geary Rd, Walnut Creek, CA 94597", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM", website: "https://www.postalannex.com/" },
    { name: "Costco — Richmond", address: "2400 Monument Blvd, Concord, CA 94520", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM", website: "https://www.costco.com/" },
  ],
  dmvLocations: [
    {
      name: "DMV — Berkeley",
      address: "1515 Clinton Ave, Alameda, CA 94501",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Oakland",
      address: "5300 Claremont Ave, Oakland, CA 94618",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — El Cerrito",
      address: "6400 Manila Ave, El Cerrito, CA 94530",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Walnut Creek",
      address: "1910 N Broadway, Walnut Creek, CA 94596",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Contra Costa County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "15 miles from downtown Berkeley",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "The closest international airport for Berkeley travelers. Direct flights to Mexico, Europe, and Asia.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "25 miles from Berkeley",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via BART or I-80.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
    {
      code: "SJC",
      name: "San Jose International Airport",
      distance: "45 miles from Berkeley",
      address: "1701 Airport Blvd, San Jose, CA 95110",
      note: "Convenient for South Bay travelers. Domestic flights and some international connections.",
      flightUrl: "https://www.flysanjose.com/",
      flightLabel: "San Jose Airport",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the California Department of Public Health Vital Records. Online, mail, or in-person options available for Alameda County residents.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through California CDPH Vital Records. Required for estate and legal matters in the East Bay.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Alameda County Clerk-Recorder. Certified copies available for name changes and legal purposes in Berkeley.", url: "https://www.acgov.org/auditor/clerk/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Alameda County Superior Court. Required for name change passport corrections and legal documentation.", url: "https://www.alameda.courts.ca.gov/" },
  ],
};